import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useAction, useList } from "@/lib/api/queries";
import { ApiError } from "@/lib/api/errors";
import { roleLabel } from "@/lib/labels";
import { ValueField, defaultText, parseValue, type Definition } from "./value";

/**
 * Yangi qoralama taklif qilish.
 *
 * Qiymat MAYDONI ta'rifdagi turga qarab quriladi: mantiqiy
 * bayroq uchun ikkita tugma, ro'yxat va obyekt uchun JSON
 * maydoni. Bitta umumiy matn maydoni qoldirilsa, xodim
 * `true` o'rniga `"true"` yuborib, serverdan tushunarsiz
 * validatsiya xatosi olardi.
 *
 * Qiymat DARHOL kuchga kirmaydi: u qoralama bo'lib turadi va
 * tasdiqlanishi kerak (TZ §20.1) — shuning uchun formada
 * "saqlash" emas, "taklif qilish" deyiladi. Tizimda ikkinchi
 * tasdiqlovchi bo'lsa, tasdiqni BOSHQA odam beradi; yolg'iz
 * adminli o'rnatmada muallif o'zi tasdiqlay oladi, aks holda
 * birorta sozlama faollasha olmasdi.
 */

interface Version {
  id: string;
  key: string;
  version: number;
}

export function NewDraftDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const definitions = useList<Definition>("config", "/api/v1/config-definitions", {}, { enabled: open });
  const [key, setKey] = useState("");
  const [scope, setScope] = useState("");
  const [raw, setRaw] = useState("");
  const [reason, setReason] = useState("");

  const create = useAction<Version, { key: string; body: unknown }>("config", ({ key: target, body }) => ({
    path: `/api/v1/config/${target}/versions`,
    method: "POST",
    body,
  }));

  const dialogRef = useRef<HTMLDialogElement>(null);

  // `showModal` ATAYLAB: oddiy `<dialog open>` sahifa oqimida
  // qolib, jadvaldan pastda ochilardi. Modal rejimda brauzer
  // uni ustki qatlamga chiqaradi, fonni bloklaydi va Esc bilan
  // yopilishini o'zi ta'minlaydi.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const items = definitions.data?.items ?? [];
  const definition = items.find((item) => item.key === key);

  // Tanlangan sozlama o'zgarganda qiymat maydoni STANDART bilan
  // to'ldiriladi: bo'sh maydon "nima yozish kerak" degan savolni
  // qoldirardi, standart esa kutilgan shaklni ko'rsatadi.
  const reset = (nextKey: string) => {
    setKey(nextKey);
    const next = items.find((item) => item.key === nextKey);
    setRaw(next ? defaultText(next) : "");
    setScope(next?.scope_kind === "GLOBAL_ONLY" ? "GLOBAL" : "");
    create.reset();
  };

  const parsed = useMemo(() => parseValue(definition, raw), [definition, raw]);
  const ready = Boolean(definition) && parsed.ok && reason.trim().length > 0;

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className="dialog dialog-wide glass-thick"
      aria-label="Yangi qoralama"
      onClose={onClose}
    >
      <div className="dialog-head">
        <div className="dialog-icon">
          <Icon name="plus" size={18} />
        </div>
        <div>
          <div className="dialog-title">Yangi qiymat taklif qilish</div>
          <div className="dialog-desc">
            Qiymat darhol kuchga kirmaydi: u qoralama bo'lib qoladi va
            «Versiyalar tarixi» da tasdiqlanib, ishga tushiriladi.
          </div>
        </div>
      </div>

      <label className="field">
        <span className="field-label">Sozlama</span>
        <select className="input" value={key} onChange={(event) => reset(event.target.value)}>
          <option value="">— tanlang —</option>
          {/* Ro'yxatda o'zbekcha tavsif turadi: kalitni (`sla.manager_decision`)
              o'qib sozlamani tanib olish mumkin emas edi. */}
          {items.map((item) => (
            <option key={item.key} value={item.key}>
              {item.description_uz ? `${item.description_uz} (${item.key})` : item.key}
            </option>
          ))}
        </select>
        {definition && (
          <span className="field-hint">
            {definition.description_uz}
            {definition.par_id ? ` · ${definition.par_id}` : ""}
            {definition.owner_role ? ` · ${roleLabel(definition.owner_role)}` : ""}
          </span>
        )}
      </label>

      {definition && (
        <>
          {/* Qamrov: faqat ta'rif ruxsat bergan joyda so'raladi.
              GLOBAL_ONLY sozlamada korpus maydonini ko'rsatish
              serverga baribir rad etiladigan qiymat yuborishga
              undardi. */}
          {definition.scope_kind === "WAREHOUSE" ? (
            <label className="field">
              <span className="field-label">Qamrov</span>
              <input
                className="input"
                value={scope}
                onChange={(event) => setScope(event.target.value.toUpperCase())}
                placeholder="Korpus kodi (A, B, C) yoki bo'sh — GLOBAL"
              />
              <span className="field-hint">
                Bo'sh qoldirilsa qiymat butun tizim uchun amal qiladi.
              </span>
            </label>
          ) : (
            <p className="field-hint">Bu sozlama faqat GLOBAL qamrovda o'rnatiladi.</p>
          )}

          <ValueField definition={definition} value={raw} onChange={setRaw} error={parsed.ok ? undefined : parsed.error} />
        </>
      )}

      <label className="field">
        <span className="field-label">Sabab (majburiy)</span>
        <textarea
          className="input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Nega o'zgartirilyapti — auditda shu matn qoladi"
        />
      </label>

      {create.error && (
        <div className="dialog-error">
          <Icon name="alert" size={16} />
          <span>
            {create.error instanceof ApiError ? create.error.body.message : String(create.error)}
          </span>
        </div>
      )}

      <div className="dialog-actions">
        <button type="button" className="btn btn-text" onClick={onClose} disabled={create.isPending}>
          Bekor qilish
        </button>
        <button
          type="button"
          className="btn btn-filled"
          disabled={!ready || create.isPending}
          onClick={() => {
            if (!definition || !parsed.ok) return;
            create.mutate(
              {
                key: definition.key,
                body: {
                  scope: definition.scope_kind === "GLOBAL_ONLY" ? "GLOBAL" : scope || "GLOBAL",
                  value: parsed.value,
                  reason: reason.trim(),
                },
              },
              {
                onSuccess: () => {
                  onCreated();
                  onClose();
                },
              },
            );
          }}
        >
          Taklif qilish
        </button>
      </div>
    </dialog>
  );
}
